<script setup lang="ts" generic="Value extends File">
import { computed } from 'vue';
import { container } from '@shared/common/dependency-injection/domain/container';
import type { FileFormField } from '@shared/common/form/domain/form/field/file.form-field';
import type { FormFieldFileComponent } from '@shared/common/form/application/component/file/form-field-file-component.types';
import { FormDependencyTypes } from '@shared/common/form/form.dependency-types';
import FormFieldMessages from '@shared/common/form/application/component/FormFieldMessages.vue';

const model = defineModel<FileFormField>({ required: true });
const component = container.get<FormFieldFileComponent>(FormDependencyTypes.FormFieldFileComponent);

const fileValue = computed({
  // NOTE: The design-system InputFile treats `modelValue === undefined` as "uncontrolled"
  // (it keeps its internal file). In our domain, `undefined` means "unchanged".
  // To ensure UI correctly resets (ex: cancel changes), we map `undefined` => `null` for the DS component.
  get: (): File | null => (model.value.value === undefined ? null : model.value.value),
  set: (value: File | null) => {
    model.value.value = value;
  },
});

function onRemove(): void {
  model.value.value = null;
  model.value.previewUrl = undefined;
  model.value.fileName = undefined;

  model.value.onRemove?.();
}
</script>

<template>
  <div>
    <component
      :is="component"
      :id="model.id"
      v-model="fileValue"
      :label="model.label"
      :hint="model.hint"
      :disabled="model.disabled"
      :accept="model.accept"
      :button-label="model.buttonLabel"
      :placeholder="model.placeholder"
      :preview-url="model.previewUrl"
      :file-name="model.fileName"
      @remove="onRemove"
    />
    <form-field-messages :errors="model.errorMessages" />
  </div>
</template>
